import { FC, ReactNode } from 'react';
import { Tooltip } from '@ds';
import { employeeLabelName } from '@/constants/resources.constants';
import { ResourceAvatar, ResourceAvatarProps } from '../ResourceAvatar';

export interface CustomUserProps {
  avatarProps: ResourceAvatarProps;
  title: string;
  description?: string;
  titleSize: 'lg' | 'md' | 'sm';
  descriptionSize?: 'lg' | 'md';
  titleColor: 'black' | 'lightBlack';
  itemsGap?: 'lg' | 'md';
  textGap?: 'sm' | 'none';
  itemsAlignment?: 'start' | 'center';
  isPin?: boolean;
  showIcon?: boolean;
  icon?: ReactNode;
  truncated?: boolean;
  children?: ReactNode;
}

const titleSizes = {
  lg: 'text-l-m',
  md: 'text-m-m',
  sm: 'text-s-sb',
};

const descriptionSizes = {
  lg: 'text-m-r',
  md: 'text-s-r',
};

const containerGaps = {
  lg: 'gap-3',
  md: 'gap-2',
};

const titleColors = {
  black: 'text-black',
  lightBlack: 'text-base-5',
};

const iconClasses = {
  pin: 'text-black',
  default: 'inline-flex items-center justify-center p-1 bg-base-55 rounded-full',
};

export const TmdxUser: FC<CustomUserProps> = ({
  avatarProps,
  title,
  description,
  titleSize = 'md',
  descriptionSize = 'md',
  titleColor = 'lightBlack',
  itemsGap = 'md',
  itemsAlignment = 'center',
  textGap = 'sm',
  isPin = false,
  showIcon,
  icon,
  truncated = true,
  children,
  ...props
}) => {
  return (
    <div
      className={`flex flex-row ${truncated ? 'truncate' : ''} ${
        itemsAlignment === 'center' ? 'items-center' : 'items-start'
      } ${containerGaps[itemsGap]}`}
      {...props}
    >
      <ResourceAvatar className="shrink-0" {...avatarProps}>
        {!!avatarProps.label && employeeLabelName[avatarProps.label]}
      </ResourceAvatar>

      <div
        className={`flex flex-col items-start ${truncated ? 'truncate' : ''} ${textGap === 'sm' ? 'gap-0.5' : 'gap-0'}`}
      >
        <div className={`flex w-full flex-row items-center gap-2 ${truncated ? 'truncate' : ''}`}>
          <p className={`${titleSizes[titleSize]} ${titleColors[titleColor]} ${truncated ? 'truncate' : ''}`}>
            {title}
          </p>

          {showIcon && <span className={`${isPin ? iconClasses.pin : iconClasses.default}`}>{icon}</span>}
        </div>

        {description && (
          <Tooltip className="p-0" content={<span className="px-2">{description}</span>}>
            <p
              className={`${descriptionSizes[descriptionSize]} text-base-30 w-full ${
                truncated ? 'truncate' : ''
              } text-left`}
            >
              {description}
            </p>
          </Tooltip>
        )}

        {children}
      </div>
    </div>
  );
};
